/**
 * Helpers for internal-transfer credit accounts (CMF vs external own).
 * Contract: entregable-2 contrato-cuentas-propias-externas.md
 */

/**
 * @param {object|null|undefined} creditAccount
 * @returns {boolean}
 */
export function isExternalOwnCreditAccount(creditAccount) {
    return Boolean(creditAccount && creditAccount.externalOwn === true);
}

/**
 * Preview / draft: keep the object the form already holds.
 *
 * @param {object} creditAccount
 * @returns {object}
 */
export function serializeCreditAccountForPreview(creditAccount) {
    return creditAccount;
}

/**
 * Send / tad.send:
 * - CMF → idProduct (legacy)
 * - external own → cbu (transactional id)
 *
 * @param {object} creditAccount
 * @returns {string|undefined}
 */
export function serializeCreditAccountForSend(creditAccount) {
    if (isExternalOwnCreditAccount(creditAccount)) {
        return creditAccount.cbu;
    }
    return creditAccount && creditAccount.idProduct;
}

/**
 * Normalize `externalOwnAccounts` from transfers.internal.pre.
 * Flag off / missing → empty list.
 *
 * @param {*} externalOwnAccounts
 * @returns {Array}
 */
export function normalizeExternalOwnAccounts(externalOwnAccounts) {
    return Array.isArray(externalOwnAccounts) ? externalOwnAccounts : [];
}

/**
 * @param {string|null|undefined} productType
 * @returns {string|undefined}
 */
export function inferCurrencyCodeFromProductType(productType) {
    if (!productType || typeof productType !== "string") {
        return undefined;
    }
    const match = productType.toUpperCase().match(/\b(ARS|USD|EUR)\b/);
    return match ? match[1] : undefined;
}

/**
 * Map agenda external-own entry to AccountSelect / AccountOption row (no balance).
 * V3 badge layout (`showBankBrand` + non-CMF): mainText = account number, description = bank name.
 *
 * @param {object} externalAccount
 * @param {{ currencyLabel?: string }} [opts]
 * @returns {object}
 */
export function mapExternalOwnAccountToOption(externalAccount, { currencyLabel = "" } = {}) {
    return {
        id: externalAccount.cbu,
        accountName: externalAccount.accountNumber || externalAccount.cbu || "",
        description: externalAccount.localBank || externalAccount.productType || "",
        currency: currencyLabel,
        codeBank: externalAccount.codeBank,
        externalOwn: true,
    };
}

/**
 * Merge CMF selector options with external own accounts for credit picker.
 *
 * @param {object} params
 * @param {Array} params.cmfOptions
 * @param {Array} [params.externalOwnAccounts]
 * @param {string} params.debitAccountId
 * @param {string} [params.debitCurrencyLabel]
 * @param {string} [params.debitCbu]
 * @param {(code: string) => string} [params.getCurrencyLabel]
 * @returns {Array}
 */
/**
 * When own UX is OFF, drop external-own credit from drafts/templates so the form cannot
 * silently send an external destination (Inc 9.6).
 *
 * @param {*} creditAccount
 * @param {boolean} ownUxEnabled
 * @returns {*}
 */
export function sanitizeInternalCreditWhenOwnDisabled(creditAccount, ownUxEnabled) {
    if (ownUxEnabled) {
        return creditAccount;
    }
    if (isExternalOwnCreditAccount(creditAccount)) {
        return undefined;
    }
    if (typeof creditAccount === "string" && creditAccount.length === 22) {
        return undefined;
    }
    return creditAccount;
}

export function buildInternalCreditAccountOptions({
    cmfOptions = [],
    externalOwnAccounts = [],
    debitAccountId,
    debitCurrencyLabel,
    debitCbu,
    getCurrencyLabel,
    ownUxEnabled = true,
}) {
    const cmfFiltered = cmfOptions.filter(
        (option) => option.id !== debitAccountId && (!debitCurrencyLabel || option.currency === debitCurrencyLabel),
    );

    if (!ownUxEnabled) {
        return cmfFiltered;
    }

    const externalOptions = [];
    normalizeExternalOwnAccounts(externalOwnAccounts).forEach((externalAccount) => {
        if (!externalAccount || !externalAccount.cbu) {
            return;
        }
        if (debitCbu && externalAccount.cbu === debitCbu) {
            return;
        }

        const currencyCode = inferCurrencyCodeFromProductType(externalAccount.productType);
        const currencyLabel =
            currencyCode && typeof getCurrencyLabel === "function"
                ? getCurrencyLabel(currencyCode)
                : debitCurrencyLabel || "";

        if (debitCurrencyLabel && currencyCode && currencyLabel !== debitCurrencyLabel) {
            return;
        }

        externalOptions.push(mapExternalOwnAccountToOption(externalAccount, { currencyLabel }));
    });

    return [...cmfFiltered, ...externalOptions];
}

/**
 * Resolve selected credit id to CMF product or external-own contract object.
 *
 * @param {string|undefined|null} selectionId
 * @param {Array} accounts
 * @param {Array} externalOwnAccounts
 * @param {boolean} [ownUxEnabled=true]
 * @returns {object|undefined}
 */
export function resolveInternalCreditAccount(selectionId, accounts, externalOwnAccounts, ownUxEnabled = true) {
    if (selectionId == null || selectionId === "") {
        return undefined;
    }

    const cmfAccount = (accounts || []).find((account) => account.idProduct === selectionId);
    if (cmfAccount) {
        return cmfAccount;
    }

    if (!ownUxEnabled) {
        return undefined;
    }

    const externalAccount = normalizeExternalOwnAccounts(externalOwnAccounts).find(
        (account) => account.cbu === selectionId,
    );
    if (!externalAccount) {
        return undefined;
    }

    return {
        externalOwn: true,
        idFrequentDestination: externalAccount.idFrequentDestination,
        cbu: externalAccount.cbu,
        accountNumber: externalAccount.accountNumber,
        localBank: externalAccount.localBank,
        codeBank: externalAccount.codeBank,
        destinationType: externalAccount.destinationType,
        productType: externalAccount.productType,
        recipientName: externalAccount.recipientName,
    };
}

export function isInternalCreditCvu(creditAccount) {
    if (creditAccount == null || creditAccount === "") {
        return false;
    }
    if (typeof creditAccount === "string") {
        return creditAccount.length === 22 && creditAccount.startsWith("000");
    }
    if (creditAccount.destinationType === "CVU") {
        return true;
    }
    const { cbu } = creditAccount;
    return typeof cbu === "string" && cbu.length === 22 && cbu.startsWith("000");
}

export function resolveInternalSendActivityId({ ownUxEnabled, cvuCoelsaEnabled, reduxIsCvu = false, creditAccount }) {
    const creditIsCvu = isInternalCreditCvu(creditAccount);
    if (ownUxEnabled && cvuCoelsaEnabled && (reduxIsCvu || creditIsCvu)) {
        return "transfersCVU.internal.send";
    }
    return "transfers.internal.send";
}
